/**
 * SOLID Review
 * S — Strong. One use case: the profile opened — the account as the server has it, kept and answered.
 * O — Strong. What is kept is the Domain's decision.
 * L — Strong. Implements its use-case role exactly.
 * I — Strong. `execute`.
 * D — Strong. The decisions and auth-server ports and the session's step, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type { ICommandProfileLoaded, IPayloadProfile } from '../../domain/ports/schemas/AuthMoves';
import type { TPortResult } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { IConfigDepAuth } from './IConfigDepAuth';

/**
 * The profile opened (FE3): the account as the server has it, kept and answered — the modal fills its nickname field from
 * it. Not a round trip: the read comes first, and a failed read keeps what the store holds.
 */
export class LoadProfileUseCase implements IUseCase<ICommandNothing, TPortResult<IPayloadProfile | null>> {
  constructor(private readonly deps: IConfigDepAuth) {}

  async execute(ctx: IContextRequest, _command: ICommandNothing): Promise<TPortResult<IPayloadProfile | null>> {
    const { decisions, step, auth } = this.deps;
    const me = await auth.me(ctx);
    if (!me.success) return me;
    if (me.value === null) return { success: true, value: null };
    const { displayName, email, matchCredits } = me.value;
    const profile: IPayloadProfile = { displayName, email, matchCredits };
    const loaded: ICommandProfileLoaded = { profile };
    const kept = await step.run(ctx, (input) => decisions.profileLoaded(ctx, input, loaded));
    if (!kept.success) return kept;
    return { success: true, value: profile };
  }
}
